import { useEffect, useState, useMemo } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Spinner } from "@/components/ui/spinner";
import { api } from "@/utils/api";
import { SystemUserRole, SYSTEM_ROLES } from "@/schemas/systemUserRole";

// Define form schema with validation
const createStaffAccountSchema = () =>
  z.object({
    username: z.string().min(2, "用戶名至少需要2個字元"),
    password: z.string().min(6, "密碼至少需要6個字元"),
  });

interface StaffAccountFormProps {
  onComplete: (userId: string) => void;
  companyId: string | null;
  email: string;
  role?: SystemUserRole;
}

const StaffAccountForm = ({
  onComplete,
  companyId,
  email,
  role = SYSTEM_ROLES.USER,
}: StaffAccountFormProps) => {
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState<SystemUserRole>(SYSTEM_ROLES.USER);

  // Get role from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const roleFromUrl = urlParams.get("role") as SystemUserRole;
    if (roleFromUrl && (roleFromUrl === SYSTEM_ROLES.ADMIN || roleFromUrl === SYSTEM_ROLES.USER)) {
      setUserRole(roleFromUrl);
    } else if (role === SYSTEM_ROLES.ADMIN) {
      setUserRole(SYSTEM_ROLES.ADMIN);
    } else {
      setUserRole(SYSTEM_ROLES.USER);
    }
  }, [role]);

  const staffAccountSchema = useMemo(() => createStaffAccountSchema(), []);
  type StaffAccountFormValues = z.infer<typeof staffAccountSchema>;

  // Initialize the form with default values and validation schema
  const form = useForm<StaffAccountFormValues>({
    resolver: zodResolver(staffAccountSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // Handle form submission
  const onSubmit = async (values: StaffAccountFormValues) => {
    if (!companyId) {
      toast({
        title: "錯誤",
        description: "缺少公司信息",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Call the backend endpoint for staff account setup
      const res = await api.post("/staff-account/setup", {
        email: email,
        username: values.username,
        password: values.password,
        companyId: companyId,
        role: userRole,
      });

      toast({
        title: "成功",
        description: "帳號創建成功",
      });

      onComplete(res.data.id);
    } catch (error: any) {
      console.error("Staff signup error:", error);
      toast({
        title: "錯誤",
        description: error.message || "帳號創建失敗",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold">完成帳號設定</h1>
        <p className="text-gray-500 mt-2">請設定您的用戶名和密碼以完成註冊</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>用戶名</FormLabel>
                <FormControl>
                  <Input
                    placeholder="請輸入用戶名"
                    {...field}
                    disabled={loading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Email display (read-only) */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Email
            </label>
            <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
              {email}
            </div>
          </div>

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>密碼</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="請輸入密碼（至少6個字元）"
                    {...field}
                    disabled={loading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Role display (read-only) */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              角色
            </label>
            <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
              {userRole === SYSTEM_ROLES.ADMIN ? "管理員" : "一般用戶"}
            </div>
          </div>

          <Button type="submit" className="w-full mt-6" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                創建中...
              </>
            ) : (
              "完成帳號設定"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default StaffAccountForm;
